import {
  deserializeAssignments,
  DeserializedRecord,
  deserializeItem,
  deserializeManyRelationship,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIManyRelationship,
  JSONAPIRecord,
  JSONAPISingleRelationship
} from "./Helpers";
import {Information} from "./Information";
import {LoginProvider} from "./LoginProvider";
import {deserializeRole, Role} from "./Role";
import {deserializeVersion, Version, VersionDeserializer} from "./Version";
import {UploadedFile} from "helpers/forms/fields/file_field/interfaces/UploadedFile";

interface Connection extends DeserializedRecord {
  subject: DeserializedRecord;
}

interface ConnectionData extends JSONAPIRecord {
  relationships: {
    subject: JSONAPISingleRelationship;
  }
}

const deserializePhysicianConnection: Deserializer<Connection, ConnectionData> = (data, included) => {
  const subject = deserializeSingleRelationship(data.relationships.subject, included);

  return {
    ...deserializeItem(data),
    subject: subject
  };
};

interface RoleAssignment extends DeserializedRecord<"physician_role"> {
  physicianId: string;
  roleId: string;
}

interface Physician extends DeserializedRecord<"physician"> {
  connections: Connection[];
  /**
   * The latest sign in date
   */
  currentSignInAt: Date;
  profilePicture: string | File | UploadedFile;
  email: string;
  inactivatedAt?: Date;
  /**
   * Information about the physician without the email, since the physician itself has an email field
   */
  information: Omit<Information, "email">;
  /**
   * Set, when the user is locked due to too many incorrect login attempts
   */
  lockedAt?: Date;
  otpRequiredForLogin: boolean;
  primary?: boolean;
  /**
   * The authentication provider, used together with uid for login
   */
  provider: LoginProvider["provider"];
  roleAssignments: RoleAssignment[];
  roles: Role[];
  versions: Version<Physician>[];
  uid: string;
  uniqueId?: string;
  uniqueType?: 'idf' | 'kik' | 'lanr';
}

interface PhysicianData extends JSONAPIRecord {
  relationships: {
    connections: JSONAPIManyRelationship;
    information: JSONAPISingleRelationship;
    physician_roles: JSONAPIManyRelationship;
    roles: JSONAPIManyRelationship;
    versions: JSONAPIManyRelationship;
  }
}

const deserializePhysician: Deserializer<Physician, PhysicianData> = (data, included) => {
  const connections = deserializeManyRelationship(data.relationships?.connections, included, deserializePhysicianConnection);
  const information = deserializeSingleRelationship<Information>(data.relationships?.information, included);
  // eslint-disable-next-line @stylistic/max-len
  const roleAssignments = deserializeManyRelationship(data.relationships?.physician_roles, included, deserializeAssignments(['physician', 'role'])) as RoleAssignment[];
  const roles = deserializeManyRelationship(data.relationships?.roles, included, deserializeRole);
  const versions = deserializeManyRelationship(data.relationships?.versions, included, deserializeVersion as VersionDeserializer<Physician>);

  return {
    ...deserializeItem(data),
    connections,
    information,
    roleAssignments,
    roles,
    versions
  };
};

export type {
  Physician
};

export {
  deserializePhysician
};
